import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import type { TeacherCohortAssignmentRecord } from "../auth/api-client";
import { FloatingActionsMenu } from "./FloatingActionsMenu";

export interface CohortAssignmentActionsMenuProps {
  readonly assignment: TeacherCohortAssignmentRecord;
  readonly disabled?: boolean;
  readonly resultsHref?: string;
  readonly onAssign: () => void;
  readonly onUnassign: () => void;
  readonly onClose: () => void;
  readonly onReopen: () => void;
  readonly onDelete: () => void;
  readonly onCopyStudentLink: () => void;
}

export function CohortAssignmentActionsMenu({
  assignment,
  disabled,
  resultsHref,
  onAssign,
  onUnassign,
  onClose,
  onReopen,
  onDelete,
  onCopyStudentLink,
}: CohortAssignmentActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const showAssign = !assignment.closed && !assignment.released;
  const showUnassign = !assignment.closed && assignment.released;
  const showClose = !assignment.closed && assignment.released;
  const showReopen = assignment.closed;

  return (
    <div className="teacher-cohort-plan-card-actions">
      <button
        ref={triggerRef}
        type="button"
        className="btn secondary tight"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Actions <FontAwesomeIcon icon={faCaretDown} />
      </button>
      <FloatingActionsMenu
        anchorRef={triggerRef}
        open={open}
        onClose={() => setOpen(false)}
        className="cohort-assignment-actions-menu"
      >
          {showAssign ? (
            <button
              type="button"
              role="menuitem"
              className="teacher-actions-menu-item"
              disabled={disabled}
              onClick={() => {
                setOpen(false);
                onAssign();
              }}
            >
              Assign
            </button>
          ) : null}
          {showUnassign ? (
            <button
              type="button"
              role="menuitem"
              className="teacher-actions-menu-item"
              disabled={disabled}
              onClick={() => {
                setOpen(false);
                onUnassign();
              }}
            >
              Unassign
            </button>
          ) : null}
          {showClose ? (
            <button
              type="button"
              role="menuitem"
              className="teacher-actions-menu-item"
              disabled={disabled}
              onClick={() => {
                setOpen(false);
                onClose();
              }}
            >
              Close
            </button>
          ) : null}
          {showReopen ? (
            <button
              type="button"
              role="menuitem"
              className="teacher-actions-menu-item"
              disabled={disabled}
              onClick={() => {
                setOpen(false);
                onReopen();
              }}
            >
              Reopen
            </button>
          ) : null}
          {resultsHref ? (
            <Link
              role="menuitem"
              className="teacher-actions-menu-item"
              to={resultsHref}
              onClick={() => setOpen(false)}
            >
              View results
            </Link>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="teacher-actions-menu-item"
            disabled={disabled}
            onClick={() => {
              setOpen(false);
              onCopyStudentLink();
            }}
          >
            Copy student link
          </button>
          <button
            type="button"
            role="menuitem"
            className="teacher-actions-menu-item teacher-actions-menu-item--danger"
            disabled={disabled}
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            Delete
          </button>
      </FloatingActionsMenu>
    </div>
  );
}
